import { Component, computed, input } from '@angular/core';

import type {
  HandoffPreviousProvider,
  HandoffRow,
  HandoffSuggestedProvider,
} from '../../core/api/api.types';

export interface SummarySection {
  label: string | null;
  value: string;
  /** True for "Recent context" lines — rendered in a dimmer style. */
  isContext?: boolean;
}

/**
 * Splits the flat summaryText produced by buildOperatorSummary into labeled
 * sections. Splits on any ". " followed by a capitalized label + colon so that
 * dynamically generated fields (Service, Address, Provider note, Missing, etc.)
 * are each rendered as their own item. Falls back to a single unlabeled block.
 * City is skipped — it is already shown in the Overview card.
 */
const SPLIT_RE = /\.\s+(?=[A-Z][A-Za-z]*(?: [A-Za-z]+)*:\s*)/;

function parseSummaryText(text: string): SummarySection[] {
  const parts = text.split(SPLIT_RE).map((s) => s.trim()).filter(Boolean);

  if (parts.length <= 1) {
    return [{ label: null, value: text.trim().replace(/\.$/, '') }];
  }

  const sections: SummarySection[] = [];

  for (const part of parts) {
    const match = part.match(/^([A-Za-z ]+):\s*([\s\S]+)$/);
    if (!match) {
      sections.push({ label: null, value: part.replace(/\.$/, '') });
      continue;
    }

    const label = match[1].trim();
    const value = match[2].trim().replace(/\.$/, '');

    // Skip City — it's already in the Overview card
    if (label === 'City') continue;

    // Drop any "Suggested providers available: N" line: that number is a stale,
    // limit-capped snapshot recorded at handoff time. The authoritative list and
    // its count are rendered from suggested_providers below.
    if (/^suggested provider/i.test(label)) continue;

    sections.push({ label, value, isContext: label === 'Recent context' });
  }

  return sections;
}

@Component({
  selector: 'op-handoff-summary-card',
  imports: [],
  templateUrl: './handoff-summary-card.component.html',
  styleUrl: './handoff-summary-card.component.scss',
})
export class HandoffSummaryCardComponent {
  readonly row = input.required<HandoffRow>();

  readonly keywords = computed((): string[] => {
    const tags = this.row().ai_summary?.imageTags;
    return Array.isArray(tags) ? tags.filter((k): k is string => typeof k === 'string') : [];
  });

  readonly sections = computed((): SummarySection[] => {
    const text = this.row().ai_summary?.summaryText;
    return typeof text === 'string' && text.trim() ? parseSummaryText(text) : [];
  });

  readonly suggestedProviders = computed((): HandoffSuggestedProvider[] => {
    const list = this.row().suggested_providers;
    return Array.isArray(list) ? list : [];
  });

  /**
   * Only surfaced when the requested provider is NOT eligible for this case.
   * When they are, they lead the suggestion list with their own badge and a
   * separate notice would just repeat it.
   */
  readonly unavailablePreviousProvider = computed((): HandoffPreviousProvider | null => {
    const previous = this.row().previous_provider;
    return previous && !previous.eligible ? previous : null;
  });

  readonly isEmpty = computed(
    () =>
      !this.keywords().length &&
      !this.sections().length &&
      !this.suggestedProviders().length &&
      !this.unavailablePreviousProvider(),
  );
}